import type { LocalGame, SerializableAIConfig } from '@texas42/game-runtime';
import {
  AI_PLAY_POLICY_VERSION,
  AI_BID_POLICY_VERSION,
  AI_DECLARATION_POLICY_VERSION,
  actionToId,
  buildBidEvidence,
  buildUnscoredPlayEvidence,
  deriveDecisionRngSeed,
  isInternalAIConfig,
} from '@texas42/game-runtime';
import type { GameSessionAIClient, SessionLogger } from '../session/types.js';
import { decideLightFallbackAction } from './ai-decision-engine.js';
import { createBrowserAIWorkerTransport, type AIWorkerTransport } from './ai-worker-transport.js';
import {
  DEFAULT_AI_DEADLINE_MS,
  type AIWorkerRequest,
  type AIWorkerResponse,
  type DecisionDiagnostics,
} from './ai-worker-protocol.js';
import { PLAY_ADVICE_CANCELLED, PLAY_ADVICE_UNAVAILABLE, type PlayAdvice } from './play-advice.js';
import { toSerializableAIConfig } from '@texas42/app-model';
import type {
  AdaptivePlayerProfile,
  ProductSerializableAIConfig,
  SavedHandAiDiagnostic,
} from '@texas42/app-model';
import {
  actionSubmitDelayMs,
  DEFAULT_MIN_BID_DELAY_MS,
  remainingSubmitDelayMs,
} from './action-submit-schedule.js';

export { DEFAULT_MIN_BID_DELAY_MS, DEFAULT_MIN_PLAY_DELAY_MS } from './action-submit-schedule.js';

export interface WorkerAIClientOptions {
  deadlineMs?: number;
  transport?: AIWorkerTransport | null;
  logger?: SessionLogger;
  /**
   * Minimum ms from when a play decision is scheduled until EXECUTE_ACTION.
   * Thinking time counts; progressive-depth profiles use the shared product schedule when omitted.
   */
  minPlayDelayMs?: number;
  /**
   * Minimum ms from when a bid/pass decision is scheduled until EXECUTE_ACTION.
   * Thinking time counts; only the remainder is waited. Default 1000.
   */
  minBidDelayMs?: number;
}

interface PendingDecision {
  requestId: string;
  stateRevision: number;
  playerIndex: number;
  /** Action-history length at scheduling time, before this decision executes. */
  decisionCursor: number;
  scheduledAt: number;
  playSubmitDelayMs: number;
  cancelled: boolean;
  delayTimer?: ReturnType<typeof setTimeout>;
}

interface PendingAdvice {
  resolve: (result: PlayAdvice) => void;
  stateRevision: number;
  legalTransitionIds: Set<string>;
  deadlineTimer?: ReturnType<typeof setTimeout>;
}

function waitForActionPace(entry: PendingDecision, minDelayMs: number): Promise<void> {
  const wait = remainingSubmitDelayMs(entry.scheduledAt, minDelayMs);
  if (wait <= 0) return Promise.resolve();

  return new Promise((resolve) => {
    entry.delayTimer = setTimeout(() => {
      delete entry.delayTimer;
      resolve();
    }, wait);
  });
}

let requestCounter = 0;

function nextRequestId(): string {
  requestCounter += 1;
  return `ai-req-${requestCounter}`;
}

export function createWorkerAIClient(options: WorkerAIClientOptions = {}): GameSessionAIClient {
  const unsubscribers: Array<() => void> = [];
  const pending = new Map<string, PendingDecision>();
  const pendingAdvice = new Map<string, PendingAdvice>();
  const diagnosticsByRequest = new Map<string, DecisionDiagnostics>();
  const handDiagnostics: SavedHandAiDiagnostic[] = [];

  let paused = false;
  let transport: AIWorkerTransport | null = null;
  let workerUnavailable = false;
  let matchAiConfig: SerializableAIConfig | undefined;
  let handAiConfig: ProductSerializableAIConfig | undefined;
  let handAiConfigPromise: Promise<ProductSerializableAIConfig> | undefined;
  let getAdaptiveProfile: (() => Promise<AdaptivePlayerProfile | undefined>) | undefined;
  const deadlineMs = options.deadlineMs ?? DEFAULT_AI_DEADLINE_MS;
  const minBidDelayMs = options.minBidDelayMs ?? DEFAULT_MIN_BID_DELAY_MS;
  const logger = options.logger;
  let transportUnsubscribe: (() => void) | undefined;
  let wiredLocalGame: LocalGame | undefined;
  let getStateRevision: (() => number) | undefined;
  let pausedCheck: (() => boolean) | undefined;

  function requireMatchAiConfig(): SerializableAIConfig {
    if (!matchAiConfig) {
      throw new Error('Worker AI client is not wired with a match AI config');
    }
    return matchAiConfig;
  }

  async function currentAiConfig(): Promise<SerializableAIConfig> {
    if (handAiConfig) return handAiConfig;
    const base = requireMatchAiConfig();
    if (isInternalAIConfig(base)) return base;
    if (handAiConfigPromise) return handAiConfigPromise;

    const pendingConfig = (async () => {
      if (!getAdaptiveProfile || base.profile.id !== 'grandad') return base;
      const adaptiveProfile = await getAdaptiveProfile();
      return adaptiveProfile
        ? toSerializableAIConfig('grandad', base.aiRngSeed, adaptiveProfile)
        : base;
    })();
    handAiConfigPromise = pendingConfig;
    try {
      const config = await pendingConfig;
      if (handAiConfigPromise === pendingConfig) handAiConfig = config;
      return config;
    } finally {
      if (handAiConfigPromise === pendingConfig) handAiConfigPromise = undefined;
    }
  }

  function logWorkerUnavailableOnce(): void {
    if (!workerUnavailable) return;
    logger?.warn('AI worker unavailable; using light fallback strategy');
  }

  function cancelRequest(requestId: string): void {
    const entry = pending.get(requestId);
    if (!entry) return;

    entry.cancelled = true;
    if (entry.delayTimer !== undefined) {
      clearTimeout(entry.delayTimer);
      delete entry.delayTimer;
    }
    pending.delete(requestId);

    if (transport && !workerUnavailable) {
      transport.postMessage({ type: 'CANCEL', requestId });
    }
  }

  function cancelAllPending(): void {
    for (const requestId of [...pending.keys()]) {
      cancelRequest(requestId);
    }
  }

  function cancelAllAdvice(): void {
    for (const [requestId, entry] of pendingAdvice) {
      pendingAdvice.delete(requestId);
      if (entry.deadlineTimer !== undefined) {
        clearTimeout(entry.deadlineTimer);
      }
      transport?.postMessage({ type: 'CANCEL', requestId });
      entry.resolve(PLAY_ADVICE_CANCELLED);
    }
  }

  function handleWorkerResponse(response: AIWorkerResponse): void {
    if (!wiredLocalGame || !getStateRevision || !pausedCheck) return;

    const advice = pendingAdvice.get(response.requestId);
    if (advice) {
      pendingAdvice.delete(response.requestId);
      if (advice.deadlineTimer !== undefined) {
        clearTimeout(advice.deadlineTimer);
      }
      if (response.type === 'ADVICE') {
        if (
          response.diagnostics?.deadlineExceeded === true ||
          response.diagnostics?.fallback === true ||
          response.stateRevision !== advice.stateRevision ||
          getStateRevision() !== advice.stateRevision ||
          pausedCheck() ||
          !advice.legalTransitionIds.has(response.transitionId)
        ) {
          advice.resolve(PLAY_ADVICE_UNAVAILABLE);
          return;
        }
        advice.resolve({
          status: response.status,
          message:
            response.status === 'forced'
              ? 'This is the only legal play.'
              : response.status === 'near-equal'
                ? 'Several legal plays are near-equal; this is one strong option.'
                : 'The advisor recommends this legal play.',
          transitionId: response.transitionId,
          candidateCount: response.candidateCount,
          ...(response.diagnostics?.deadlineExceeded !== undefined
            ? { deadlineExceeded: response.diagnostics.deadlineExceeded }
            : {}),
        });
      } else {
        advice.resolve(PLAY_ADVICE_UNAVAILABLE);
      }
      return;
    }

    const entry = pending.get(response.requestId);
    if (!entry || entry.cancelled) {
      if (response.type === 'DECISION' && response.diagnostics) {
        diagnosticsByRequest.set(response.requestId, {
          ...response.diagnostics,
          cancelled: true,
        });
      }
      return;
    }

    if (pausedCheck()) {
      cancelRequest(response.requestId);
      return;
    }
    if (
      response.stateRevision !== entry.stateRevision ||
      getStateRevision() !== entry.stateRevision
    ) {
      cancelRequest(response.requestId);
      const currentRevision = getStateRevision();
      const hasCurrentRequest = [...pending.values()].some(
        (pendingEntry) =>
          pendingEntry.playerIndex === entry.playerIndex &&
          pendingEntry.stateRevision === currentRevision,
      );
      if (!hasCurrentRequest) {
        void scheduleDecision(entry.playerIndex);
      }
      return;
    }

    if (response.type === 'ERROR') {
      pending.delete(response.requestId);
      logger?.warn('AI worker decision failed', {
        requestId: response.requestId,
        message: response.message,
      });
      return;
    }

    if (response.diagnostics) {
      diagnosticsByRequest.set(response.requestId, response.diagnostics);
    }

    void submitPendingDecision(entry, response.transitionId, response.diagnostics);
  }

  async function submitPendingDecision(
    entry: PendingDecision,
    transitionId: string,
    diagnostics?: DecisionDiagnostics,
  ): Promise<void> {
    if (!wiredLocalGame || !getStateRevision || !pausedCheck) return;

    const aiClient = wiredLocalGame.aiClients.find(
      ({ playerIndex }) => playerIndex === entry.playerIndex,
    );
    if (!aiClient) {
      pending.delete(entry.requestId);
      return;
    }

    const view = aiClient.client.view;
    if (!view) {
      pending.delete(entry.requestId);
      return;
    }

    const transition = view.transitions.find((item) => item.id === transitionId);
    if (!transition) {
      pending.delete(entry.requestId);
      logger?.warn('AI worker returned stale transition', {
        transitionId,
        requestId: entry.requestId,
      });
      return;
    }

    const minActionDelayMs =
      transition.action.type === 'play'
        ? entry.playSubmitDelayMs
        : transition.action.type === 'bid' || transition.action.type === 'pass'
          ? minBidDelayMs
          : 0;
    if (minActionDelayMs > 0) {
      await waitForActionPace(entry, minActionDelayMs);
      if (entry.cancelled) return;
      if (pausedCheck()) {
        cancelRequest(entry.requestId);
        return;
      }
      if (getStateRevision() !== entry.stateRevision) {
        cancelRequest(entry.requestId);
        return;
      }
      const stillValid = aiClient.client.view?.transitions.some((item) => item.id === transitionId);
      if (!stillValid) {
        pending.delete(entry.requestId);
        return;
      }
    }

    if (entry.cancelled) return;

    pending.delete(entry.requestId);

    if (
      diagnostics &&
      (transition.action.type === 'play' ||
        transition.action.type === 'bid' ||
        transition.action.type === 'pass' ||
        transition.action.type === 'select-trump')
    ) {
      const evidence = diagnostics.evidence;
      const telemetry = { ...diagnostics };
      delete telemetry.evidence;
      delete telemetry.policyVersion;
      delete telemetry.decisionSeed;
      handDiagnostics.push({
        actionId: actionToId(transition.action),
        playerId: entry.playerIndex,
        decisionCursor: entry.decisionCursor,
        ...(diagnostics.policyVersion !== undefined
          ? { policyVersion: diagnostics.policyVersion }
          : {}),
        ...(diagnostics.decisionSeed !== undefined
          ? { decisionSeed: diagnostics.decisionSeed }
          : {}),
        ...(evidence ? { evidence } : {}),
        diagnostics: telemetry,
      });
    }

    aiClient.client.send({ type: 'EXECUTE_ACTION', action: transition.action });
  }

  async function runFallbackDecision(
    playerIndex: number,
    stateRevision: number,
    decisionCursor: number,
    scheduledAt: number,
  ): Promise<void> {
    if (!wiredLocalGame || !getStateRevision || !pausedCheck) return;
    if (pausedCheck()) return;

    const aiClient = wiredLocalGame.aiClients.find((entry) => entry.playerIndex === playerIndex);
    if (!aiClient?.client.view) return;

    const view = aiClient.client.view;
    const result = decideLightFallbackAction(
      playerIndex,
      view.validActions,
      view.state.bids,
      view.state.currentBid,
    );
    if (!result) return;
    if (getStateRevision() !== stateRevision) return;

    const transition = view.transitions.find((item) => item.id === result.transitionId);
    if (!transition) return;

    logWorkerUnavailableOnce();

    const minActionDelayMs =
      transition.action.type === 'play'
        ? actionSubmitDelayMs('play', requireMatchAiConfig(), options)
        : transition.action.type === 'bid' || transition.action.type === 'pass'
          ? minBidDelayMs
          : 0;
    if (minActionDelayMs > 0) {
      const paceEntry: PendingDecision = {
        requestId: nextRequestId(),
        stateRevision,
        playerIndex,
        decisionCursor,
        scheduledAt,
        playSubmitDelayMs: actionSubmitDelayMs('play', requireMatchAiConfig(), options),
        cancelled: false,
      };
      pending.set(paceEntry.requestId, paceEntry);
      await waitForActionPace(paceEntry, minActionDelayMs);
      const stillPending = pending.get(paceEntry.requestId);
      if (!stillPending || stillPending.cancelled) return;
      pending.delete(paceEntry.requestId);
      if (pausedCheck()) return;
      if (getStateRevision() !== stateRevision) return;
      if (!aiClient.client.view?.transitions.some((item) => item.id === result.transitionId)) {
        return;
      }
    }

    const diagnostics: DecisionDiagnostics = result.diagnostics;
    diagnosticsByRequest.set(nextRequestId(), diagnostics);
    if (
      transition.action.type === 'play' ||
      transition.action.type === 'bid' ||
      transition.action.type === 'pass' ||
      transition.action.type === 'select-trump'
    ) {
      const playActions = view.validActions.filter(
        (validAction) =>
          validAction.action.type === 'play' &&
          (!('player' in validAction.action) || validAction.action.player === playerIndex),
      );
      const fallbackAction = { action: transition.action, label: transition.label };
      const evidence = diagnostics.evidence ??
        (transition.action.type === 'play'
          ? buildUnscoredPlayEvidence(playActions, fallbackAction, 'fallback')
          : transition.action.type === 'bid' || transition.action.type === 'pass'
            ? buildBidEvidence([], fallbackAction, [], 'unscored', view.state.bids)
            : undefined);
      const telemetry = { ...diagnostics };
      delete telemetry.evidence;
      delete telemetry.policyVersion;
      delete telemetry.decisionSeed;
      handDiagnostics.push({
        actionId: actionToId(transition.action),
        playerId: playerIndex,
        decisionCursor,
        policyVersion:
          diagnostics.policyVersion ??
          (transition.action.type === 'play'
            ? AI_PLAY_POLICY_VERSION
            : transition.action.type === 'select-trump'
              ? AI_DECLARATION_POLICY_VERSION
              : AI_BID_POLICY_VERSION),
        decisionSeed:
          diagnostics.decisionSeed ??
          deriveDecisionRngSeed(requireMatchAiConfig().aiRngSeed, view.state, playerIndex),
        ...(evidence ? { evidence } : {}),
        diagnostics: telemetry,
      });
    }
    aiClient.client.send({ type: 'EXECUTE_ACTION', action: transition.action });
  }

  async function scheduleDecision(playerIndex: number): Promise<void> {
    if (!wiredLocalGame || !getStateRevision || !pausedCheck) return;
    if (pausedCheck()) return;

    const aiClient = wiredLocalGame.aiClients.find((entry) => entry.playerIndex === playerIndex);
    if (!aiClient?.client.view) return;

    const view = aiClient.client.view;
    const stateRevision = getStateRevision();
    const myActions = view.validActions.filter((validAction) => {
      if (!('player' in validAction.action)) return true;
      return validAction.action.player === playerIndex;
    });
    if (myActions.length === 0) return;

    const requestId = nextRequestId();
    const scheduledAt = Date.now();
    const decisionCursor = view.state.actionHistory.length;

    for (const [existingId, existing] of pending.entries()) {
      if (existing.playerIndex === playerIndex) {
        cancelRequest(existingId);
      }
    }

    if (workerUnavailable || !transport) {
      await runFallbackDecision(playerIndex, stateRevision, decisionCursor, scheduledAt);
      return;
    }

    const entry: PendingDecision = {
      requestId,
      stateRevision,
      playerIndex,
      decisionCursor,
      scheduledAt,
      playSubmitDelayMs: actionSubmitDelayMs('play', requireMatchAiConfig(), options),
      cancelled: false,
    };
    pending.set(requestId, entry);

    const config = await currentAiConfig();
    if (
      pending.get(requestId) !== entry ||
      entry.cancelled ||
      pausedCheck() ||
      getStateRevision() !== stateRevision
    ) {
      cancelRequest(requestId);
      return;
    }
    entry.playSubmitDelayMs = actionSubmitDelayMs('play', config, options);

    const request: AIWorkerRequest = {
      type: 'DECIDE',
      requestId,
      stateRevision,
      playerId: playerIndex,
      viewState: view.state,
      validActions: view.validActions,
      config,
      deadlineMs,
    };

    transport.postMessage(request);
  }

  function teardownTransport(): void {
    transportUnsubscribe?.();
    transportUnsubscribe = undefined;
    transport?.terminate();
    transport = null;
  }

  function ensureTransport(): void {
    if (options.transport !== undefined) {
      transport = options.transport;
      workerUnavailable = transport === null;
      return;
    }

    transport = createBrowserAIWorkerTransport();
    workerUnavailable = transport === null;
  }

  async function requestPlayAdvice(input: {
    playerId: number;
    stateRevision: number;
    viewState: import('@texas42/game-runtime').FilteredGameState;
    validActions: import('@texas42/game-runtime').ValidAction[];
  }): Promise<PlayAdvice> {
    if (!transport || workerUnavailable) return PLAY_ADVICE_UNAVAILABLE;

    cancelAllAdvice();
    const requestId = nextRequestId();
    return new Promise<PlayAdvice>((resolve) => {
      const entry: PendingAdvice = {
        resolve,
        stateRevision: input.stateRevision,
        legalTransitionIds: new Set(
          input.validActions
            .filter(
              (validAction) =>
                validAction.action.type === 'play' &&
                (!('player' in validAction.action) || validAction.action.player === input.playerId),
            )
            .map((validAction) => actionToId(validAction.action)),
        ),
      };
      entry.deadlineTimer = setTimeout(() => {
        const pendingEntry = pendingAdvice.get(requestId);
        if (!pendingEntry) return;
        pendingAdvice.delete(requestId);
        transport?.postMessage({ type: 'CANCEL', requestId });
        pendingEntry.resolve(PLAY_ADVICE_UNAVAILABLE);
      }, deadlineMs);
      pendingAdvice.set(requestId, entry);
      void currentAiConfig()
        .then((config) => {
          if (!pendingAdvice.has(requestId)) return;
          if (!transport || workerUnavailable) {
            const entry = pendingAdvice.get(requestId);
            pendingAdvice.delete(requestId);
            if (entry?.deadlineTimer !== undefined) {
              clearTimeout(entry.deadlineTimer);
            }
            entry?.resolve(PLAY_ADVICE_UNAVAILABLE);
            return;
          }
          transport.postMessage({
            type: 'ADVISE',
            requestId,
            stateRevision: input.stateRevision,
            playerId: input.playerId,
            viewState: input.viewState,
            validActions: input.validActions,
            config,
            deadlineMs,
          });
        })
        .catch(() => {
          const entry = pendingAdvice.get(requestId);
          if (!entry) return;
          pendingAdvice.delete(requestId);
          if (entry.deadlineTimer !== undefined) {
            clearTimeout(entry.deadlineTimer);
          }
          entry.resolve(PLAY_ADVICE_UNAVAILABLE);
        });
    });
  }

  return {
    wire(localGame, wireOptions) {
      cancelAllPending();
      cancelAllAdvice();
      for (const unsubscribe of unsubscribers) unsubscribe();
      unsubscribers.length = 0;
      teardownTransport();

      wiredLocalGame = localGame;
      getStateRevision = wireOptions.getStateRevision;
      pausedCheck = () => paused || wireOptions.paused();
      matchAiConfig = wireOptions.aiConfig;
      handAiConfig = wireOptions.restoredHandAiConfig;
      handAiConfigPromise = undefined;
      getAdaptiveProfile = wireOptions.getAdaptiveProfile;

      ensureTransport();

      if (transport) {
        transportUnsubscribe = transport.subscribe(handleWorkerResponse);
      } else {
        workerUnavailable = true;
      }

      for (const { client, playerIndex } of localGame.aiClients) {
        const unsubscribe = client.subscribe(() => {
          void scheduleDecision(playerIndex);
        });
        unsubscribers.push(unsubscribe);
      }
    },

    pause() {
      paused = true;
      cancelAllPending();
      cancelAllAdvice();
    },

    resume() {
      paused = false;
      this.nudge();
    },

    nudge() {
      if (!wiredLocalGame || paused) return;
      for (const { playerIndex } of wiredLocalGame.aiClients) {
        void scheduleDecision(playerIndex);
      }
    },

    dispose() {
      cancelAllPending();
      cancelAllAdvice();
      for (const unsubscribe of unsubscribers) unsubscribe();
      unsubscribers.length = 0;
      teardownTransport();
      wiredLocalGame = undefined;
      getStateRevision = undefined;
      pausedCheck = undefined;
      getAdaptiveProfile = undefined;
      matchAiConfig = undefined;
      handAiConfig = undefined;
      handAiConfigPromise = undefined;
      paused = false;
    },

    getHandAiConfig() {
      return handAiConfig;
    },

    drainHandDiagnostics() {
      const drained = [...handDiagnostics];
      handDiagnostics.length = 0;
      handAiConfig = undefined;
      handAiConfigPromise = undefined;
      return drained;
    },
    requestPlayAdvice,
    cancelPlayAdvice: cancelAllAdvice,
  };
}

export function resetWorkerAIClientRequestCounter(): void {
  requestCounter = 0;
}
